import { i18n } from "shared/i18n";

/**
 * F1 -- the concepts MARSAD assumes you already know.
 *
 * "Concept taught" scored ZERO on every step for every persona in the
 * walkthrough. The product uses IBS, MVB, impact tolerance, blast
 * radius, three positions, dual sign-off and hash chain as though the
 * reader arrived knowing them, and the only place any of it is
 * explained is a documentation site they are not on.
 *
 * The pattern already exists in the product -- the war room, the
 * fragility radar and the tolerance dimensions pages each explain
 * their model in two sentences and answer a business question in the
 * heading. This is that, made reusable.
 *
 * Round 9 G -- the copy used to live here, on the reasoning that
 * these are DEFINITIONS rather than interface strings: the same
 * sentence wherever the term appears, and splitting them across a
 * catalogue of button labels is how they drift. That reasoning holds
 * and is preserved -- they sit together under one `glossary`
 * namespace in en.ts.
 *
 * What did not hold was the sentence that followed it: "translation
 * follows the same route as the rest -- `ar.ts` wraps this module's
 * English". `ar.ts` wraps `en`, and this module was not in it, so
 * every definition rendered as bare English under the pseudo-locale.
 * A stated assumption, disproved by measuring.
 */

export interface GlossaryTerm {
  /** The term as a reader meets it. */
  term: string;
  /** Other things it is called, for search. */
  aka?: string[];
  /** One or two plain sentences. No jargon inside the definition. */
  short: string;
  /** The bit a curious reader wants next. Optional. */
  more?: string;
  /**
   * ROUND 13 H6, thirteenth round of asking -- where you DO this.
   *
   * Twenty-seven terms, every one of them a definition and none of
   * them a route. A glossary that can tell a reader what a thing is
   * and not where it is done teaches the word and not the product,
   * which is the complaint this item has carried.
   *
   * Optional, because a few of these are concepts rather than
   * surfaces, and inventing a destination for one would be worse than
   * leaving it as the definition it is.
   */
  where?: string;
}

/**
 * The terms, and the other names they go by.
 *
 * Round 9 G -- the WORDS moved to shared/i18n/en.ts under `glossary`.
 * What stays is the closed set of terms and their search aliases: a
 * hard-coded English definition here is one `markUntranslated` never
 * sees, so it could not translate at any price.
 */
const TERM_ALIASES: Record<string, string[]> = {
  ibs: ["IBS", "important business service"],
  admission: ["IBS admission", "admitted"],
  impact_tolerance: ["tolerance", "MTPD", "maximum tolerable"],
  mvb: ["MVB"],
  gate1_threshold: ["MVB threshold", "gate 1"],
  dual_signoff: ["two signatures", "sign-off"],
  positions: ["preparer", "reviewer", "approver", "three positions"],
  cascade: ["blast radius", "blast"],
  hash_chain: ["chain", "tamper-evident"],
  /* Round 20 Y8 -- "sealed" with nothing to inspect. */
  seal: ["seal", "sealed", "seal hash", "row hash"],
  control_testing: ["three tests", "ToD", "ToI", "ToE"],
  // Round 17 R6 -- the strip on every IBS, finding, letter and scenario.
  thread: ["the thread", "five stages"],
  maturity_level: ["ML", "maturity"],
  finding: ["findings"],
  /* Round 12 H2 -- neither of these had an entry, which is why two
     controls were pointed at `finding` instead. */
  cap: ["CAP", "corrective action plan", "remediation plan", "action plan"],
  kpi: ["KPI", "KPIs", "effectiveness KPI", "metric"],
  tier: ["tier", "tier 0", "tier 1", "tier 2"],
  service_classification: ["classification"],
  connector: ["connectors", "cadence", "freshness"],
  architecture_edges: ["architecture edges", "edges", "IBS to service link"],
  data_source_class: ["class", "canonical", "reference", "demo"],
  data_baseline: ["baseline", "linkage", "discrimination"],
  supervisory_check: ["checks", "field exam"],
  resilience_score: ["score", "tier band", "confidence"],
  examiner_grant: ["grant", "examiner access"],
  assessment_cycle: ["cycle"],
  control_status: ["compliant", "partial", "non-compliant", "no evidence"],
  peer_workflow: ["PEER", "workflow", "draft", "in review"],
  probe: ["probes", "implementation signal"],
  drra: ["DRRA", "self-assessment"],
  /* ROUND 13 H6, fourth round -- the constructs the product invented
     and never defined. A reader meeting the six-layer block, the
     capability city or a data asset for the first time had no entry
     to go to, on a page whose whole job is that. */
  data_asset: ["data asset", "data", "residency"],
  dependency_layers: ["six layers", "layers", "dependency stack"],
  capability_city: ["capability city", "city", "3D city"],
  propagation: ["propagation", "downstream", "reaches"],
  estate: ["estate", "the estate"],
  /* Round 16 H31 -- every construct new since round 14, which a
     reader met with nowhere to look it up. */
  critical_internal_function: [
    "CIF",
    "critical internal function",
    "internal function",
  ],
  funding_edge: ["funding", "funds", "funded by"],
  integrity_tolerance: ["integrity", "running without integrity"],
  fail_closed: ["fail closed", "stop safely"],
  ibs_group: ["group", "IBS group", "Indirect Services"],
  route_2: ["Route 2", "second door", "CRO and CIO"],
  contagion: ["contagion", "F4"],
  tolerance_window: ["window", "month end", "overnight"],
  tolerance_limit: ["limit", "warning limit"],
  stated_duration: ["stated duration", "duration"],
  owner_roles: ["business owner", "service owner", "submitter"],
  /* Round 19 G39 -- the change register's construct had no entry. */
  significant_change: ["significant change", "change register", "changes"],
  routine_change: ["routine change"],
};

/**
 * ROUND 13 H6 -- where the reader DOES the thing.
 *
 * Thirteenth round of asking. A route is not given for a term that has
 * no single place -- a concept the product explains rather than a
 * surface it offers -- because a destination invented to fill a column
 * sends a reader somewhere that does not answer them.
 */
const TERM_ROUTES: Record<string, string> = {
  ibs: "/foundation/admin/ibses",
  admission: "/twin/ibs-admission",
  impact_tolerance: "/twin/tolerance-metrics",
  mvb: "/twin/mvb-candidates",
  gate1_threshold: "/foundation/admin/mvb-declarations",
  dual_signoff: "/my-work",
  positions: "/my-work",
  cascade: "/twin/cascade",
  hash_chain: "/admin/chains",
  control_testing: "/framework/assessments",
  maturity_level: "/readiness",
  finding: "/twin/cascade",
  cap: "/framework",
  kpi: "/framework/assessments",
  service_classification: "/foundation/admin/services",
  connector: "/admin/connectors",
  architecture_edges: "/twin/constellation",
  data_source_class: "/admin/bank-setup",
  data_baseline: "/admin/data-baseline",
  supervisory_check: "/twin/field-exam",
  resilience_score: "/readiness",
  examiner_grant: "/admin/examiner-grants",
  assessment_cycle: "/framework/assessments",
  peer_workflow: "/framework",
  probe: "/framework/assessments",
  drra: "/framework/attestations",
  data_asset: "/foundation/admin/data-assets",
  dependency_layers: "/twin/ibs-portfolio",
  capability_city: "/twin/constellation",
  critical_internal_function: "/twin/cifs",
  funding_edge: "/twin/cifs",
  integrity_tolerance: "/twin/tolerance-metrics",
  fail_closed: "/twin/tolerance-metrics",
  ibs_group: "/twin/ibs-groups",
  route_2: "/twin/cifs",
  owner_roles: "/my-work",
  significant_change: "/twin/changes",
  routine_change: "/twin/changes",
  // `tier`, `control_status`, `propagation` and `estate` are read
  // everywhere and done nowhere: they describe how a value is arrived
  // at rather than a place a person goes.
};

/**
 * Search keys that are the REGULATOR's word rather than the product's.
 *
 * The definitions became regime-aware for free when the catalogue's
 * literals became interpolations (P415): `glossaryTerms.ibs.term` is
 * `{{serviceTermTitle}}`, so it already reads "Critical Operation"
 * under OSFI. Search did not follow, because these aliases are a
 * hard-coded list -- so a Canadian reader typing the only word their
 * regulator uses would find nothing, and conclude the product has no
 * entry for it.
 *
 * Derived from the same `defaultVariables` the strings interpolate
 * through, so the index and the heading cannot disagree: two lists of
 * one regulator's words is the drift this track exists to remove.
 *
 * SEARCH ONLY, and the vocabulary drive is what settled that. The
 * first version merged these into the rendered "Also called" line, and
 * the drive reported the Kingdom's glossary changing -- "impact
 * tolerance, impact tolerances, headroom" appearing under a heading
 * that already said Impact tolerance. Track J's whole claim is that
 * nothing SAMA sees changes, so a cosmetic improvement that breaks it
 * is not an improvement. The alias line is prose a reader reads; this
 * is an index. Only the index needed to learn a second regulator's
 * words.
 *
 * `riskLimitTerm` is deliberately absent: a risk limit is a different
 * concept from a tolerance, and indexing one under the other would
 * make search confidently wrong rather than silently incomplete.
 */
const REGIME_ALIAS_KEYS: Record<string, readonly string[]> = {
  ibs: ["serviceTerm", "serviceTermShort", "serviceTermPlural"],
  impact_tolerance: ["toleranceTerm", "toleranceTermPlural"],
  mvb: ["mvbTerm", "mvbTermShort"],
  examiner_grant: ["examinerTerm"],
};

/** The active regime's terms, as the interpolation layer holds them. */
function activeTerms(): Record<string, string> {
  const vars = i18n.options.interpolation?.defaultVariables;
  return (vars ?? {}) as Record<string, string>;
}

function regimeAliases(key: string): string[] {
  const terms = activeTerms();
  const out: string[] = [];
  for (const vocabKey of REGIME_ALIAS_KEYS[key] ?? []) {
    const value = terms[vocabKey];
    // Skip the untranslated-scaffold guillemets: "\u2039critical operation\u203a"
    // is not a word anybody searches for.
    if (typeof value === "string" && value && !value.includes("\u2039"))
      out.push(value);
  }
  return out;
}

/**
 * One term, composed in the reader's language.
 *
 * `i18n.t` rather than the hook: this is called from render and from
 * tests, and every call site already subscribes to the language.
 */
export function glossaryTerm(key: string): GlossaryTerm | undefined {
  if (!(key in TERM_ALIASES)) return undefined;
  const term = i18n.t(`glossaryTerms.${key}.term`);
  // A missing key returns the key itself, which would put
  // "glossaryTerms.ibs.term" on screen where a definition belongs.
  if (term.startsWith("glossaryTerms.")) return undefined;
  /*
   * `more` is OPTIONAL, and asking i18next for a key that does not
   * exist logs a missing-key error -- 429 of them in CI, which treats
   * an unexpected console.error in a test as a failure. My local run
   * passed: the guard is a CI-side rule, so the first place this could
   * show was the place I had not looked.
   *
   * `exists` is the question actually being asked: does this term have
   * a further paragraph, rather than did the lookup fall through.
   */
  const hasMore = i18n.exists(`glossaryTerms.${key}.more`);
  const where = TERM_ROUTES[key];
  return {
    term,
    aka: TERM_ALIASES[key] ?? [],
    short: i18n.t(`glossaryTerms.${key}.short`),
    ...(hasMore ? { more: i18n.t(`glossaryTerms.${key}.more`) } : {}),
    ...(where ? { where } : {}),
  };
}

/** Back-compatible view: the same record the module used to export. */
export const GLOSSARY: Record<string, GlossaryTerm> = new Proxy(
  {},
  {
    get: (_t, key: string) => glossaryTerm(key),
    has: (_t, key: string) => key in TERM_ALIASES,
    ownKeys: () => Object.keys(TERM_ALIASES),
    getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true }),
  },
) as Record<string, GlossaryTerm>;

/** Every key, in the order the glossary page shows them. */
export const GLOSSARY_KEYS = Object.keys(GLOSSARY);

/**
 * Terms matching a search query, best first. Deliberately simple: a
 * term, its aliases, then its definition. Nobody needs fuzzy matching
 * to find "IBS".
 */
export function findTerms(query: string): { key: string; term: GlossaryTerm }[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const scored: { key: string; term: GlossaryTerm; score: number }[] = [];
  for (const [key, term] of Object.entries(GLOSSARY)) {
    const names = [term.term, ...(term.aka ?? []), ...regimeAliases(key)].map((n) =>
      n.toLowerCase(),
    );
    let score = 0;
    if (names.some((n) => n === q)) score = 3;
    else if (names.some((n) => n.includes(q))) score = 2;
    else if (term.short.toLowerCase().includes(q)) score = 1;
    if (score > 0) scored.push({ key, term, score });
  }
  scored.sort((a, b) => b.score - a.score || a.term.term.localeCompare(b.term.term));
  return scored.map(({ key, term }) => ({ key, term }));
}
